import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import * as pdfjsLib from "pdfjs-dist";
import { FileDown, X, Loader2, ExternalLink, AlertTriangle, ZoomIn, ZoomOut, RotateCcw, Printer } from "lucide-react";
import { toast } from "sonner";

pdfjsLib.GlobalWorkerOptions.workerSrc = "https://unpkg.com/pdfjs-dist@4.0.379/build/pdf.worker.min.mjs";

// Anteprima PDF renderizzando le pagine su canvas (niente iframe/blob: robusto rispetto a CSP frame-src).
// Renderizzata via portal su document.body con z-index alto e pointer-events attivi, così resta
// visibile e cliccabile anche quando è aperta all'interno di un Dialog Radix modale.
export default function PdfPreviewModal({ doc, filename, onClose, title = "Anteprima documento" }) {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [zoom, setZoom] = useState(1);
  const scrollRef = useRef(null);

  // Il modale è renderizzato via portal fuori dal Dialog Radix: lo scroll-lock (react-remove-scroll)
  // blocca la rotella/trackpad. Gestiamo lo scroll manualmente sul contenitore per riabilitarlo.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (e.ctrlKey) return; // lascia passare lo zoom del browser (ctrl+rotella)
      e.preventDefault();
      el.scrollTop += e.deltaY;
      el.scrollLeft += e.deltaX;
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [loading, pages.length]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const buf = doc.output("arraybuffer");
        const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
        const imgs = [];
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale: 2 });
          const canvas = document.createElement("canvas");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
          imgs.push(canvas.toDataURL("image/png"));
        }
        if (!cancelled) { setPages(imgs); setLoading(false); }
      } catch (e) {
        if (!cancelled) {
          setFailed(true);
          setLoading(false);
          toast.error("Anteprima a schermo non riuscita. Usa 'Apri in nuova finestra' o 'Scarica PDF'.");
        }
      }
    })();
    return () => { cancelled = true; };
  }, [doc]);

  // Chiudi con Escape solo l'anteprima (intercetta prima dei dialog sottostanti, così non si esce dall'editor).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); if (e.stopImmediatePropagation) e.stopImmediatePropagation(); onClose(); }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  const openInNewWindow = () => {
    try {
      const url = doc.output("bloburl");
      const w = window.open(url, "_blank");
      if (!w || w.closed || typeof w.closed === "undefined") {
        toast.error("Popup bloccato dal browser. Consenti i popup per questo sito e riprova.");
      }
    } catch (e) {
      toast.error("Impossibile aprire il PDF in una nuova finestra.");
    }
  };

  const printPdf = () => {
    try {
      const url = doc.output("bloburl");
      const iframe = document.createElement("iframe");
      iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
      iframe.src = url;
      iframe.onload = () => {
        try { iframe.contentWindow.focus(); iframe.contentWindow.print(); }
        catch (e) { window.open(url, "_blank"); }
      };
      document.body.appendChild(iframe);
    } catch (e) {
      toast.error("Stampa non riuscita. Usa 'Scarica PDF'.");
    }
  };

  const modal = (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4" style={{ pointerEvents: "auto" }} data-testid="copertina-preview">
      <div className="bg-[#121824] border border-slate-700 rounded-lg shadow-2xl w-full max-w-[95vw] flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
          <div className="font-head font-semibold text-slate-100 flex items-center gap-2"><FileDown className="h-4 w-4 text-amber-400" /> {title}</div>
          <div className="flex items-center gap-2">
            {pages.length > 0 && (
              <div className="flex items-center gap-1 mr-1" data-testid="pdf-zoom-controls">
                <button type="button" onClick={() => setZoom((z) => Math.max(0.5, Math.round((z - 0.1) * 10) / 10))} data-testid="pdf-zoom-out" title="Riduci" className="h-7 w-7 flex items-center justify-center rounded-md text-slate-300 hover:text-amber-400 hover:bg-slate-700/50"><ZoomOut className="h-4 w-4" /></button>
                <span data-testid="pdf-zoom-value" className="text-xs font-mono text-slate-200 w-11 text-center tabular-nums">{Math.round(zoom * 100)}%</span>
                <button type="button" onClick={() => setZoom((z) => Math.min(2.5, Math.round((z + 0.1) * 10) / 10))} data-testid="pdf-zoom-in" title="Ingrandisci" className="h-7 w-7 flex items-center justify-center rounded-md text-slate-300 hover:text-amber-400 hover:bg-slate-700/50"><ZoomIn className="h-4 w-4" /></button>
                <button type="button" onClick={() => setZoom(1)} data-testid="pdf-zoom-reset" title="Reimposta zoom" className="h-7 w-7 flex items-center justify-center rounded-md text-slate-300 hover:text-amber-400 hover:bg-slate-700/50"><RotateCcw className="h-4 w-4" /></button>
              </div>
            )}
            <button type="button" onClick={onClose} data-testid="copertina-preview-close" className="text-slate-400 hover:text-slate-100"><X className="h-5 w-5" /></button>
          </div>
        </div>
        <div ref={scrollRef} className="flex-1 min-h-0 overflow-auto bg-slate-800/40 p-4" data-testid="pdf-preview-scroll">
          {loading ? (
            <div className="h-[60vh] flex items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Generazione anteprima…</div>
          ) : pages.length ? (
            <div className="space-y-4 flex flex-col items-center">
              {pages.map((src, i) => <img key={i} src={src} alt={`Pagina ${i + 1}`} style={{ width: `calc((95vw - 3rem) * ${zoom})`, maxWidth: "none" }} className="shadow-lg rounded" />)}
            </div>
          ) : (
            <div className="h-[60vh] flex flex-col items-center justify-center text-center gap-3 text-slate-300" data-testid="preview-fallback">
              <AlertTriangle className="h-8 w-8 text-amber-400" />
              <div className="text-sm">{failed ? "Non è stato possibile mostrare l'anteprima a schermo." : "Nessun contenuto da mostrare."}</div>
              <button type="button" onClick={openInNewWindow} data-testid="preview-open-window" className="flex items-center gap-2 text-sm rounded-md bg-cyan-600 hover:bg-cyan-700 text-white px-3 py-1.5">
                <ExternalLink className="h-4 w-4" /> Apri in nuova finestra
              </button>
            </div>
          )}
        </div>
        <div className="flex flex-wrap justify-end gap-2 px-4 py-3 border-t border-slate-700 shrink-0">
          <button type="button" onClick={openInNewWindow} data-testid="preview-open-window-footer" className="flex items-center gap-2 text-sm rounded-md border border-slate-600 text-slate-200 hover:bg-slate-800 px-3 py-1.5"><ExternalLink className="h-4 w-4" /> Apri in nuova finestra</button>
          <button type="button" onClick={printPdf} data-testid="pdf-print" className="flex items-center gap-2 text-sm rounded-md border border-slate-600 text-slate-200 hover:bg-slate-800 px-3 py-1.5"><Printer className="h-4 w-4" /> Stampa</button>
          <button type="button" onClick={onClose} className="text-sm rounded-md border border-slate-600 text-slate-200 hover:bg-slate-800 px-3 py-1.5">Chiudi</button>
          <button type="button" data-testid="copertina-download" onClick={() => doc.save(filename)} className="flex items-center gap-2 text-sm rounded-md bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold px-3 py-1.5"><FileDown className="h-4 w-4" /> Scarica PDF</button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
